// メッシュワープ (純ロジック)。4×4 の制御点によるベジェ曲面 (Photoshop のワープと同じ) と、
// 「ソース → 出力」の頂点列を三角形ごとに前方マッピングして描く汎用レンダラ。
import type { Pt, Rect } from '@/types/geometry'
import type { RgbaImage } from './transform'

export interface MeshVertex {
  sx: number
  sy: number
  dx: number
  dy: number
}

function bilinear(img: RgbaImage, x: number, y: number, out: Uint8ClampedArray, o: number): void {
  const { width: w, height: h, data } = img
  const cx = Math.max(0, Math.min(w - 1.001, x)), cy = Math.max(0, Math.min(h - 1.001, y))
  const x0 = Math.floor(cx), y0 = Math.floor(cy), tx = cx - x0, ty = cy - y0
  const x1 = Math.min(w - 1, x0 + 1), y1 = Math.min(h - 1, y0 + 1)
  const i00 = (y0 * w + x0) * 4, i10 = (y0 * w + x1) * 4, i01 = (y1 * w + x0) * 4, i11 = (y1 * w + x1) * 4
  const a00 = data[i00 + 3]! * (1 - tx) * (1 - ty), a10 = data[i10 + 3]! * tx * (1 - ty), a01 = data[i01 + 3]! * (1 - tx) * ty, a11 = data[i11 + 3]! * tx * ty
  const asum = a00 + a10 + a01 + a11
  if (asum <= 0) return
  for (let c = 0; c < 3; c++) out[o + c] = (data[i00 + c]! * a00 + data[i10 + c]! * a10 + data[i01 + c]! * a01 + data[i11 + c]! * a11) / asum
  out[o + 3] = asum
}

/**
 * (NX+1)×(NY+1) の頂点格子 (行優先) を三角形に分けて描く。dx/dy は画像原点基準の出力座標。
 * 戻り値の rect は rect (画像の文書座標) を基準にした出力の位置。
 */
export function renderMeshWarp(image: RgbaImage, rect: Rect, pts: MeshVertex[], NX: number, NY: number): { image: RgbaImage; rect: Rect } {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
  for (const p of pts) { minX = Math.min(minX, p.dx); minY = Math.min(minY, p.dy); maxX = Math.max(maxX, p.dx); maxY = Math.max(maxY, p.dy) }
  if (!Number.isFinite(minX)) return { image, rect }
  const ox = Math.floor(minX), oy = Math.floor(minY)
  const ow = Math.max(1, Math.ceil(maxX) - ox + 1), oh = Math.max(1, Math.ceil(maxY) - oy + 1)
  const out = new Uint8ClampedArray(ow * oh * 4)
  const tri = (a: MeshVertex, b: MeshVertex, c: MeshVertex) => {
    const det = (b.dx - a.dx) * (c.dy - a.dy) - (c.dx - a.dx) * (b.dy - a.dy)
    if (Math.abs(det) < 1e-9) return
    const x0 = Math.max(0, Math.floor(Math.min(a.dx, b.dx, c.dx)) - ox), x1 = Math.min(ow - 1, Math.ceil(Math.max(a.dx, b.dx, c.dx)) - ox)
    const y0 = Math.max(0, Math.floor(Math.min(a.dy, b.dy, c.dy)) - oy), y1 = Math.min(oh - 1, Math.ceil(Math.max(a.dy, b.dy, c.dy)) - oy)
    for (let y = y0; y <= y1; y++) {
      for (let x = x0; x <= x1; x++) {
        const px = x + ox + 0.5, py = y + oy + 0.5
        const l1 = ((b.dx - px) * (c.dy - py) - (c.dx - px) * (b.dy - py)) / det
        const l2 = ((c.dx - px) * (a.dy - py) - (a.dx - px) * (c.dy - py)) / det
        const l3 = 1 - l1 - l2
        const eps = -0.002
        if (l1 < eps || l2 < eps || l3 < eps) continue
        const sx = l1 * a.sx + l2 * b.sx + l3 * c.sx - 0.5, sy = l1 * a.sy + l2 * b.sy + l3 * c.sy - 0.5
        bilinear(image, sx, sy, out, (y * ow + x) * 4)
      }
    }
  }
  for (let j = 0; j < NY; j++) {
    for (let i = 0; i < NX; i++) {
      const p00 = pts[j * (NX + 1) + i]!, p10 = pts[j * (NX + 1) + i + 1]!, p01 = pts[(j + 1) * (NX + 1) + i]!, p11 = pts[(j + 1) * (NX + 1) + i + 1]!
      tri(p00, p10, p11)
      tri(p00, p11, p01)
    }
  }
  return { image: { width: ow, height: oh, data: out }, rect: { x: rect.x + ox, y: rect.y + oy, w: ow, h: oh } }
}

/** 4×4 制御点 (行優先: 上段左→右, …) のベジェ曲面上の点 */
export function bezierPatch(ctrl: Pt[], u: number, v: number): Pt {
  const b = (t: number) => { const s = 1 - t; return [s * s * s, 3 * s * s * t, 3 * s * t * t, t * t * t] }
  const bu = b(u), bv = b(v)
  let x = 0, y = 0
  for (let j = 0; j < 4; j++) for (let i = 0; i < 4; i++) {
    const w = bu[i]! * bv[j]!
    const p = ctrl[j * 4 + i]!
    x += p.x * w
    y += p.y * w
  }
  return { x, y }
}

/** 矩形を等間隔に分けた 4×4 の制御点 (変形なし) */
export function initWarpControls(rect: Rect, map?: (p: Pt) => Pt): Pt[] {
  const out: Pt[] = []
  for (let j = 0; j < 4; j++) for (let i = 0; i < 4; i++) {
    const p = { x: rect.x + (rect.w * i) / 3, y: rect.y + (rect.h * j) / 3 }
    out.push(map ? map(p) : p)
  }
  return out
}

/** 制御点がすべて等間隔の初期位置と一致するか (変形なしの判定) */
export function warpIsIdentity(rect: Rect, ctrl: Pt[], eps = 0.01): boolean {
  const init = initWarpControls(rect)
  return ctrl.every((p, i) => Math.abs(p.x - init[i]!.x) < eps && Math.abs(p.y - init[i]!.y) < eps)
}

/**
 * 画像 (文書座標 rect) をベジェ曲面 (制御点は文書座標) でワープする。
 * subdiv はメッシュの分割数 (大きいほど滑らか)。
 */
export function warpImageBezier(image: RgbaImage, rect: Rect, ctrl: Pt[], subdiv = 24): { image: RgbaImage; rect: Rect } {
  const NX = subdiv, NY = subdiv
  const pts: MeshVertex[] = []
  for (let j = 0; j <= NY; j++) {
    for (let i = 0; i <= NX; i++) {
      const u = i / NX, v = j / NY
      const d = bezierPatch(ctrl, u, v)
      pts.push({ sx: u * image.width, sy: v * image.height, dx: d.x - rect.x, dy: d.y - rect.y })
    }
  }
  return renderMeshWarp(image, rect, pts, NX, NY)
}
